import React, { useEffect, useState, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomOneLight } from "react-syntax-highlighter/dist/esm/styles/hljs";

const Markdown = ({ msg, newMexId, chatEndRef }) => {
  const [displayText, setDisplayText] = useState("");
  const streamedMessages = useRef(new Set());

  const markdownText =
    msg?.content && typeof msg.content === "string"
      ? msg.content
      : msg?.content?.message || "";

  const charsPerTick = 10;
  const intervalMs = 1;

  useEffect(() => {
    if (!markdownText || !msg?._id) return;

    const currentStreamId = sessionStorage.getItem("stream_id");
    const isNewMessage = msg._id === newMexId;

    if (isNewMessage && currentStreamId !== msg._id && !streamedMessages.current.has(msg._id)) {
      sessionStorage.setItem("stream_id", msg._id);
      streamedMessages.current.add(msg._id);
      setDisplayText("");
      let index = 0;

      const interval = setInterval(() => {
        index += charsPerTick;
        setDisplayText(markdownText.slice(0, index));

        if (chatEndRef?.current) {
          chatEndRef.current.scrollIntoView({ behavior: "smooth" });
        }

        if (index >= markdownText.length) {
          clearInterval(interval);
        }
      }, intervalMs);

      return () => clearInterval(interval);
    } else {
      setDisplayText(markdownText);
      if (chatEndRef?.current) {
        chatEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [msg._id, newMexId, markdownText, chatEndRef]);

  if (!markdownText) return null;

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ node, ...props }) => (
          <h1 style={{ fontSize: "18px", fontWeight: 600, color: "#181a22" }} {...props} />
        ),
        h2: ({ node, ...props }) => (
          <h2 style={{ fontSize: "16px", fontWeight: 600, color: "#181a22" }} {...props} />
        ),
        h3: ({ node, ...props }) => (
          <h3 style={{ fontSize: "15px", fontWeight: 500, color: "#181a22" }} {...props} />
        ),
        p: ({ node, ...props }) => (
          <p style={{ fontSize: "14px", color: "#181a22", lineHeight: 1.5 }} {...props} />
        ),
        code({ inline, className, children, ...props }) {
          const match = /language-(\w+)/.exec(className || "");
          return !inline && match ? (
            <SyntaxHighlighter
              style={atomOneLight}
              language={match[1]}
              PreTag="div"
              customStyle={{
                fontSize: "12px",
                borderRadius: "20px",
                padding: "15px",
                border: "1px solid var(--lightGrey)",
              }}
              {...props}
            >
              {String(children).replace(/\n$/, "")}
            </SyntaxHighlighter>
          ) : (
            <code
              style={{
                padding: "2px 4px",
                borderRadius: "4px",
                fontSize: "12px",
                color: "#181a22",
              }}
              {...props}
            >
              {children}
            </code>
          );
        },
      }}
    >
      {displayText}
    </ReactMarkdown>
  );
};

export default Markdown;
